import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Server, Globe2, ShieldCheck, ArrowUpRight } from 'lucide-react';

const formatNumber = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M';
  if (num >= 1000) return Number(num).toLocaleString('en-US');
  return num;
};

const StatsCards = ({ data, isLoading }) => {
  const { t, language } = useLanguage();

  const stats = [
    {
      title: t('totalActiveDevices'),
      value: data?.total_nodes ?? 0,
      icon: Server,
      accent: 'text-primary',
      tag: 'NODES',
      note: language === 'tr' ? 'Global dağıtım · canlı' : 'Global distribution · live',
      live: true,
    },
    {
      title: t('totalDetectedIPs'),
      value: formatNumber(data?.total_ips ?? 0),
      icon: Globe2,
      accent: 'text-cyan-400',
      tag: 'IPS · CUMULATIVE',
      note: language === 'tr' ? 'Tüm zamanlar toplam' : 'All-time total',
    },
    {
      title: t('currentlyListedIPs'),
      value: formatNumber(data?.current_ips ?? 0),
      icon: ShieldCheck,
      accent: 'text-amber-400',
      tag: 'IPS · ACTIVE',
      note: language === 'tr' ? 'Aktif engel listesi' : 'Active blocklist',
    },
  ];

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="border border-border bg-card p-5">
            <div className="animate-pulse space-y-3">
              <div className="h-3 w-24 bg-muted" />
              <div className="h-10 w-32 bg-muted" />
              <div className="h-3 w-40 bg-muted" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="stats-cards">
      {stats.map((stat, index) => {
        const Icon = stat.icon;
        return (
          <div
            key={index}
            className="relative border border-border bg-card hover:border-primary/50 transition-colors corner-ticks group"
            data-testid={`stat-card-${index}`}
          >
            {/* Header strip */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/30">
              <div className="flex items-center gap-2">
                <Icon className={`h-3.5 w-3.5 ${stat.accent}`} strokeWidth={2.25} />
                <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
                  {stat.tag}
                </span>
              </div>
              {stat.live && (
                <span className="flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-[0.18em] text-primary">
                  <span className="status-dot" />
                  LIVE
                </span>
              )}
            </div>

            {/* Body */}
            <div className="p-5">
              <div className="flex items-end justify-between gap-3">
                <div>
                  <div className={`font-mono tabular text-4xl font-bold leading-none ${stat.accent}`}>
                    {stat.value}
                  </div>
                  <div className="mt-3 text-[11px] font-mono uppercase tracking-[0.14em] text-foreground">
                    {stat.title}
                  </div>
                  <div className="mt-1 text-[10px] font-mono text-muted-foreground tracking-wider">
                    // {stat.note}
                  </div>
                </div>
                <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default StatsCards;
